import React from "react";

import { useQuery } from "@apollo/client";
import {
  Box,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  TextField,
} from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import { makeStyles } from "tss-react/mui";

import SearchIcon from "@/components/Icons/SearchIcon";
import Padding from "@/components/layout/Padding";
import { graphql } from "@/gql";
import useSearchText from "@/hooks/useSearchText";
import { colors } from "@/theme/overrides/colors";
import Margin from "@components/layout/Margin";

import EmptyView from "./EmptyView";
import MessageListItem from "./MessageListItem";

const useStyles = makeStyles()((theme) => ({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    paddingTop: 2,
    minHeight: 0,
    overflowY: "auto",
  },
  divider: {
    margin: theme.spacing(0, 4),
    color: colors.light,
  },
  searchInput: {
    width: "100%",
    height: 45,
    fontSize: 14,
    "& .MuiOutlinedInput-root": {
      height: 45,
      backgroundColor: "#F7F9FB",
      fontSize: "inherit",
    },
    "& .MuiInputAdornment-root": {
      fontSize: "inherit",
    },
  },
  emptySearchText: {
    fontWeight: 600,
    fontSize: 18,
    lineHeight: 1,
    color: colors.dark,
  },
}));

const messageListQuery = graphql(`
  query MessageList($search: String!) {
    chats(participantName: $search) {
      nodes {
        ...MessageListItem
      }
    }
  }
`);

const MessageList: React.FC = () => {
  const { classes } = useStyles();
  const [search, setSearch] = useSearchText({ debounce: 500 });
  const { loading, data } = useQuery(messageListQuery, {
    variables: { search },
  });

  const chats = data?.chats.nodes ?? [];

  return (
    <>
      <Padding all={4}>
        <TextField
          className={classes.searchInput}
          placeholder="Search client"
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="inherit" />
              </InputAdornment>
            ),
          }}
        />
      </Padding>
      <List className={classes.root}>
        <Virtuoso
          data={chats}
          itemContent={(_, chat) => (
            <React.Fragment key={chat.id}>
              <Divider className={classes.divider} />
              <Box>
                <MessageListItem chatId={chat.id} />
              </Box>
            </React.Fragment>
          )}
        />
        {loading && !chats && (
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        )}
        {!loading && chats.length === 0 && search === "" && (
          <Margin top={13}>
            <EmptyView />
          </Margin>
        )}
        {!loading && chats.length === 0 && search !== "" && (
          <Margin top={4}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
              }}
            >
              <div className={classes.emptySearchText}>No client found</div>
            </Box>
          </Margin>
        )}
      </List>
    </>
  );
};

export default React.memo(MessageList);
